export type MessagesQuota = {
  limit: number;
  used: number;
  remaining: number;
};

export const MESSAGES_QUOTA_WARNING_RATIO = 0.9;

export function parseMessagesQuota(raw: unknown): MessagesQuota | null {
  if (!raw || typeof raw !== 'object') return null;

  const source = raw as Record<string, unknown>;
  const limit = Number(source.limit);
  const used = Number(source.used);
  const remaining = Number(source.remaining);

  if (!Number.isFinite(limit) || limit < 0) return null;
  if (!Number.isFinite(used) || used < 0) return null;
  if (!Number.isFinite(remaining) || remaining < 0) return null;

  return { limit, used, remaining };
}

export function getMessagesQuotaUsageRatio(quota: MessagesQuota): number {
  if (quota.limit <= 0) return quota.remaining <= 0 ? 1 : 0;
  return Math.min(1, Math.max(0, quota.used / quota.limit));
}

export function isMessagesQuotaWarning(quota: MessagesQuota): boolean {
  if (quota.limit <= 0) return quota.remaining <= 0;
  return getMessagesQuotaUsageRatio(quota) >= MESSAGES_QUOTA_WARNING_RATIO;
}

export function isMessagesQuotaExceeded(quota: MessagesQuota): boolean {
  return quota.remaining <= 0;
}

export function canConsumeMessagesQuota(quota: MessagesQuota, count = 1): boolean {
  if (count <= 0) return true;
  return quota.remaining >= count;
}

export function applyMessagesQuotaConsumption(quota: MessagesQuota, count = 1): MessagesQuota {
  const safeCount = Math.max(0, count);
  const nextRemaining = Math.max(0, quota.remaining - safeCount);
  const nextUsed = Math.min(quota.limit, quota.limit - nextRemaining);

  return {
    limit: quota.limit,
    used: nextUsed,
    remaining: nextRemaining,
  };
}
